import { Injectable } from '@angular/core';
import { FiBackendService } from '../../../fi-backend/fi-backend.service';
import { LoadingService } from '../../../fi-loading/loading.service';
import {
    IChecklist2HistoryReportLineGraph,
    IChecklist2ReportAnswerDetailsData,
    IChecklist2ReportBenchDataByChecklists,
    IChecklist2ReportBenchDataByCheckpointGroups,
    IChecklist2ReportBenchDataByCheckpoints,
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportDateRange,
    IChecklist2ReportHistoryUnits,
    IChecklistV2ReportParameterFilter,
} from '../shared/fi-checklist2-report';
import { IItemsListPagedDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { isDefined } from '@fi-shared/utils';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';
import { FiImageSize, FileType } from '@fi-shared/types';

@Injectable()
export class FiChecklist2ReportHistoryService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private formatDateService: FiFormatDateService,
        private fiPrintDownloadService: FiPrintDownloadService,
    ) {}

    getChecklistHistorySummary(
        currentFilter: IChecklistV2ReportParameterFilter,
        dateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetChecklistHistorySummary?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2ReportChecklistSummary[]>(url, {
                filter: currentFilter,
                dateRange: dateRange,
            }),
        );
    }

    getChecklistUnits(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetChecklistUnits?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2ReportHistoryUnits[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getChecklistUnitIds(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetChecklistUnitIds?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<number[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getChecklistScheduleIds(checklistHistoryDateRange: IChecklist2ReportDateRange, auditMode: boolean) {
        const url = `Checklist/Checklist2ReportHistory/GetChecklistScheduleIds?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<number[]>(url, checklistHistoryDateRange));
    }

    /*getChecklistAnswerDetails(currentFilter: IChecklistV2ReportParameterFilter, checklistHistoryDateRange: IChecklist2ReportDateRange, auditMode: boolean) {
        const url = `Checklist/Checklist2ReportHistory/GetChecklistAnswerDetails?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<IChecklist2ReportAnswer[]>(url, {
            filter: currentFilter,
            dateRange: checklistHistoryDateRange,
        }));
    }*/

    getDataForLineGraph(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetDataForLineGraph?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2HistoryReportLineGraph[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getBenchDataByChecklists(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetBenchDataByChecklists?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2ReportBenchDataByChecklists[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getBenchDataByCheckpoints(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetBenchDataByCheckpoints?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2ReportBenchDataByCheckpoints[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getBenchDataByCheckpointGroups(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetBenchDataByCheckpointGroups?auditMode=${!!auditMode}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IChecklist2ReportBenchDataByCheckpointGroups[]>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    getDetailedAnswers(
        currentFilter: IChecklistV2ReportParameterFilter,
        checklistHistoryDateRange: IChecklist2ReportDateRange,
        auditMode: boolean,
        page: number,
    ) {
        const url = `Checklist/Checklist2ReportHistory/GetDetailedAnswers?auditMode=${!!auditMode}&page=${page}`;
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<IItemsListPagedDataFetchResult<IChecklist2ReportAnswerDetailsData>>(url, {
                filter: currentFilter,
                dateRange: checklistHistoryDateRange,
            }),
        );
    }

    downloadAnswerDetailsReport(args: {
        currentFilter: IChecklistV2ReportParameterFilter;
        checklistHistoryDateRange: IChecklist2ReportDateRange;
        reportType: FiChecklist2ReportType;
        auditMode: boolean;
        reportFormat: FileType;
        pageToDownload?: number;
    }) {
        let url = `Checklist/Checklist2ReportHistory/DownloadAnswerDetailsReport?reportType=${args.reportType}&auditMode=${!!args.auditMode}&reportFormat=${args.reportFormat}`;
        if (isDefined(args.pageToDownload)) {
            url += `&pageToDownload=${args.pageToDownload}`;
        }

        const fileName = this.fiPrintDownloadService.getReportFileName('answer_details', args.reportFormat);

        const promise = this.fiPrintDownloadService.downloadBinaryFile(url, {
            data: {
                filter: args.currentFilter,
                dateRange: args.checklistHistoryDateRange,
            },
            fileName: fileName,
            size: FiImageSize.Original,
        });

        return promise;
    }
}
